import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { userAccessAPI } from '../../../services/api';
import type { UiFeaturePolicy } from '../../../types/uiAccessPolicy';

const FEATURES = [
  {
    key: 'patient_visit.diagnosis_section',
    label: 'Diagnosis Section',
    desc: 'ICD / DSM diagnosis picker inside a patient visit.',
  },
  {
    key: 'patient_visit.glucose_log',
    label: 'Glucose Log',
    desc: 'Blood glucose tracking and trend chart inside a patient visit.',
  },
];

type ConfigMeta = { name: string; entityId: string; entityName: string };

export default function FeatureAccessMatrix() {
  const { activeTenantId, tenants } = useBusiness();
  const { policy, updatePolicy } = useFeatureAccess();

  const [localPolicy, setLocalPolicy] = useState<UiFeaturePolicy>(policy);
  const [configsByEntity, setConfigsByEntity] = useState<Map<string, Array<{ config_id: string; name: string }>>>(
    new Map(),
  );
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  // one open entity per feature key
  const [openEntity, setOpenEntity] = useState<Record<string, string | null>>({});
  // search query per feature key
  const [search, setSearch] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Active tenant's entity list
  const activeEntities = useMemo(
    () => tenants.find((t) => t.tenant_id === activeTenantId)?.entities ?? [],
    [tenants, activeTenantId],
  );

  // Load configs for every entity in the tenant
  useEffect(() => {
    if (!activeTenantId || activeEntities.length === 0) return;
    setLoadingConfigs(true);
    Promise.all(
      activeEntities.map((e) =>
        userAccessAPI
          .listEntityConfigs(activeTenantId, e.entity_id)
          .then((res) => ({ entityId: e.entity_id, configs: res.data ?? [] }))
          .catch(() => ({ entityId: e.entity_id, configs: [] })),
      ),
    )
      .then((results) => {
        const map = new Map<string, Array<{ config_id: string; name: string }>>();
        for (const r of results) map.set(r.entityId, r.configs);
        setConfigsByEntity(map);
      })
      .finally(() => setLoadingConfigs(false));
  }, [activeTenantId, activeEntities]);

  // Flat config meta lookup: configId → { name, entityId, entityName }
  const configMeta = useMemo<Map<string, ConfigMeta>>(() => {
    const map = new Map<string, ConfigMeta>();
    for (const entity of activeEntities) {
      const configs = configsByEntity.get(entity.entity_id) ?? [];
      for (const cfg of configs) {
        map.set(cfg.config_id, {
          name: cfg.name,
          entityId: entity.entity_id,
          entityName: entity.name,
        });
      }
    }
    return map;
  }, [activeEntities, configsByEntity]);

  // Sync when backend policy loads
  useEffect(() => {
    setLocalPolicy(policy);
  }, [policy]);

  // ── helpers ────────────────────────────────────────────────────────────────

  const getRule = (featureKey: string) =>
    localPolicy.features[featureKey] ?? { enabledByDefault: true, allowedConfigIds: [] };

  const patchRule = (featureKey: string, patch: Partial<UiFeaturePolicy['features'][string]>) => {
    setLocalPolicy((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: { ...getRule(featureKey), ...patch },
      },
    }));
  };

  const toggleConfig = (featureKey: string, configId: string, checked: boolean) => {
    const current = getRule(featureKey).allowedConfigIds ?? [];
    const next = checked ? [...current, configId] : current.filter((id) => id !== configId);
    patchRule(featureKey, { allowedConfigIds: next });
  };

  const removeConfig = (featureKey: string, configId: string) => {
    const current = getRule(featureKey).allowedConfigIds ?? [];
    patchRule(featureKey, { allowedConfigIds: current.filter((id) => id !== configId) });
  };

  const clearRestriction = (featureKey: string) => {
    patchRule(featureKey, { allowedConfigIds: [] });
  };

  const disableEverywhere = (featureKey: string) => {
    patchRule(featureKey, { enabledByDefault: false, allowedConfigIds: [] });
  };

  const toggleEntity = (featureKey: string, entityId: string) => {
    setOpenEntity((prev) => ({
      ...prev,
      [featureKey]: prev[featureKey] === entityId ? null : entityId,
    }));
  };

  const onSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await updatePolicy(localPolicy);
      setSaveMsg({ type: 'success', text: 'Feature policies saved.' });
    } catch {
      setSaveMsg({ type: 'error', text: 'Failed to save feature policies.' });
    } finally {
      setSaving(false);
    }
  };

  // ── filtered entity list for a feature ─────────────────────────────────────

  const filteredEntities = (featureKey: string) => {
    const q = (search[featureKey] ?? '').toLowerCase().trim();
    if (!q) return activeEntities;
    return activeEntities.filter((e) => {
      if (e.name.toLowerCase().includes(q)) return true;
      const configs = configsByEntity.get(e.entity_id) ?? [];
      return configs.some((c) => c.name.toLowerCase().includes(q));
    });
  };

  const filteredConfigs = (featureKey: string, entityId: string) => {
    const q = (search[featureKey] ?? '').toLowerCase().trim();
    const configs = configsByEntity.get(entityId) ?? [];
    if (!q) return configs;
    const entityName = activeEntities.find((e) => e.entity_id === entityId)?.name.toLowerCase() ?? '';
    if (entityName.includes(q)) return configs;
    return configs.filter((c) => c.name.toLowerCase().includes(q));
  };

  // ── grouped chips for the access panel ─────────────────────────────────────

  const groupedAllowed = (featureKey: string) => {
    const allowed = getRule(featureKey).allowedConfigIds ?? [];
    const groups = new Map<string, { entityName: string; items: Array<{ id: string; name: string }> }>();
    for (const id of allowed) {
      const meta = configMeta.get(id);
      if (!meta) continue;
      if (!groups.has(meta.entityId)) groups.set(meta.entityId, { entityName: meta.entityName, items: [] });
      groups.get(meta.entityId)!.items.push({ id, name: meta.name });
    }
    return Array.from(groups.values());
  };

  // ── status ─────────────────────────────────────────────────────────────────

  const statusFor = (featureKey: string) => {
    const rule = getRule(featureKey);
    const count = (rule.allowedConfigIds ?? []).length;
    if (count > 0)
      return { dot: '#1976d2', text: `Restricted — visible only for ${count} config(s). All others are blocked.` };
    if (!rule.enabledByDefault)
      return { dot: '#d32f2f', text: 'Hidden everywhere — default is off and no configs are in the allowlist.' };
    return { dot: '#2e7d32', text: 'Visible everywhere — no restriction set.' };
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Stack spacing={2}>
      {FEATURES.map((feat) => {
        const rule = getRule(feat.key);
        const allowed = rule.allowedConfigIds ?? [];
        const entities = filteredEntities(feat.key);
        const groups = groupedAllowed(feat.key);
        const status = statusFor(feat.key);
        const q = search[feat.key] ?? '';

        return (
          <Paper key={feat.key} variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* ── Header ── */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {feat.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {feat.desc}
                </Typography>
              </Box>
              <Chip
                label={feat.key}
                size="small"
                variant="outlined"
                sx={{ fontSize: '10px', height: 20, color: 'text.disabled', borderColor: 'divider', mt: 0.25 }}
              />
            </Box>

            {/* ── Body ── */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              {/* Enabled by default */}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!rule.enabledByDefault}
                    onChange={(e) => patchRule(feat.key, { enabledByDefault: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Enabled by default
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applies when no restriction is set, or when the active config is not in the allowlist.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1.5 }}
              />

              <Divider sx={{ mb: 1.5 }} />

              <Typography
                variant="caption"
                sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', display: 'block', mb: 1 }}
              >
                Restrict to specific configs
              </Typography>

              {/* ── Split: tree | access panel ── */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>

                {/* LEFT — entity accordion tree */}
                <Box>
                  <OutlinedInput
                    size="small"
                    fullWidth
                    placeholder="Search entities or configs…"
                    value={q}
                    onChange={(e) => setSearch((prev) => ({ ...prev, [feat.key]: e.target.value }))}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    }
                    sx={{ mb: 1, fontSize: '13px', '& input': { py: '6px' } }}
                  />

                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 240, overflow: 'auto', borderRadius: 1.5 }}
                  >
                    {loadingConfigs ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : activeEntities.length === 0 ? (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', p: 2, textAlign: 'center' }}>
                        No entities found.
                      </Typography>
                    ) : entities.length === 0 ? (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', p: 2, textAlign: 'center' }}>
                        No results for "{q}"
                      </Typography>
                    ) : (
                      entities.map((entity, idx) => {
                        const isOpen = openEntity[feat.key] === entity.entity_id;
                        const cfgs = filteredConfigs(feat.key, entity.entity_id);
                        const selectedCount = (configsByEntity.get(entity.entity_id) ?? []).filter((c) =>
                          allowed.includes(c.config_id),
                        ).length;

                        return (
                          <Box
                            key={entity.entity_id}
                            sx={{ borderBottom: idx < entities.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                          >
                            {/* Entity row */}
                            <Box
                              onClick={() => toggleEntity(feat.key, entity.entity_id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.875,
                                cursor: 'pointer',
                                bgcolor: isOpen ? 'grey.50' : 'transparent',
                                '&:hover': { bgcolor: 'grey.50' },
                                userSelect: 'none',
                              }}
                            >
                              <ChevronRightIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'text.disabled',
                                  flexShrink: 0,
                                  transition: 'transform 0.18s ease',
                                  transform: isOpen ? 'rotate(90deg)' : 'none',
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, fontSize: '12px' }}>
                                {entity.name}
                              </Typography>
                              {selectedCount > 0 && (
                                <Chip
                                  label={selectedCount}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '& .MuiChip-label': { px: '6px' },
                                  }}
                                />
                              )}
                            </Box>

                            {/* Config sub-list */}
                            <Collapse in={isOpen} unmountOnExit>
                              <Box sx={{ bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                                {cfgs.length === 0 ? (
                                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 4, py: 1 }}>
                                    No configs found.
                                  </Typography>
                                ) : (
                                  cfgs.map((cfg, ci) => (
                                    <Box
                                      key={cfg.config_id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        pl: 3.5,
                                        pr: 1.25,
                                        py: 0.25,
                                        borderBottom: ci < cfgs.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        '&:hover': { bgcolor: 'action.hover' },
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={allowed.includes(cfg.config_id)}
                                        onChange={(e) => toggleConfig(feat.key, cfg.config_id, e.target.checked)}
                                        sx={{ p: 0.5 }}
                                      />
                                      <Typography variant="caption" sx={{ flex: 1, ml: 0.75, color: 'text.primary' }}>
                                        {cfg.name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '10px' }}>
                                        {cfg.config_id}
                                      </Typography>
                                    </Box>
                                  ))
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    )}
                  </Paper>
                </Box>

                {/* RIGHT — current access panel */}
                <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', borderRadius: 1.5, overflow: 'hidden' }}>
                  {/* Panel header */}
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.875,
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled' }}
                    >
                      Current Access
                    </Typography>
                    {allowed.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ fontSize: '11px', minWidth: 0, p: 0, color: 'text.secondary', textTransform: 'none' }}
                        onClick={() => clearRestriction(feat.key)}
                      >
                        Clear all
                      </Button>
                    )}
                  </Box>

                  {/* Chips */}
                  <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 200, px: 1.5, py: 1.25 }}>
                    {groups.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 72 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', lineHeight: 1.8 }}>
                          No restriction set.{'\n'}Follows "Enabled by default".
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {groups.map((group) => (
                          <Box key={group.entityName}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '10px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '.05em',
                                color: 'text.disabled',
                                display: 'block',
                                mb: 0.5,
                              }}
                            >
                              {group.entityName}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {group.items.map((item) => (
                                <Chip
                                  key={item.id}
                                  label={item.name}
                                  size="small"
                                  variant="outlined"
                                  onDelete={() => removeConfig(feat.key, item.id)}
                                  sx={{ fontSize: '11px', height: 22 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Disable footer */}
                  <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      sx={{ fontSize: '11px', minWidth: 0, p: 0, textTransform: 'none' }}
                      onClick={() => disableEverywhere(feat.key)}
                    >
                      Disable for every config
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* ── Status bar ── */}
            <Box
              sx={{
                px: 2,
                py: 0.875,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: status.dot,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {status.text}
              </Typography>
            </Box>
          </Paper>
        );
      })}

      {/* ── Save ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button variant="contained" onClick={() => void onSave()} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : 'Save Feature Policies'}
        </Button>
        {saving && <CircularProgress size={16} />}
      </Box>

      {saveMsg && (
        <Alert severity={saveMsg.type} onClose={() => setSaveMsg(null)}>
          {saveMsg.text}
        </Alert>
      )}
    </Stack>
  );
}
